import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import "./index.css";
import { Provider, useSelector } from "react-redux";
import App from "./App";
import store from "./store/store";
import ToastConfig from "./config/toastConfig";

// Theme wrapper component that reads darkmode from REDUX (not useDarkMode hook)
const ThemedApp = () => {
  // Read darkmode from Redux store instead of useDarkMode hook
  const darkmode = useSelector((state) => state.theme?.darkmode ?? false);

  // Create MUI theme based on darkmode state
  const theme = createTheme({
    palette: {
      mode: darkmode ? "dark" : "light",
      primary: {
        main: "#f59e0b", // Amber
        light: "#fbbf24",
        dark: "#d97706",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#d97706",
        light: "#f59e0b",
        dark: "#b45309",
      },
      background: {
        default: darkmode ? "#0f172a" : "#f9fafb",
        paper: darkmode ? "#1e293b" : "#ffffff",
      },
      text: {
        primary: darkmode ? "#f1f5f9" : "#111827",
        secondary: darkmode ? "#94a3b8" : "#6b7280",
      },
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: "'DM Sans', 'Poppins', system-ui, sans-serif",
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: "16px",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "16px",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: "16px",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
            },
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
      <ToastConfig />
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <BrowserRouter
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    }}
  >
    <Provider store={store}>
      <ThemedApp />
    </Provider>
  </BrowserRouter>,
);
